/**
 * Dominant-colour extraction from album artwork.
 *
 * The deck and the glass chrome both retint from whatever record is on the
 * platter, so this runs for every track — including cross-origin YouTube
 * thumbnails, where reading pixels back is simply not allowed.
 */

import { hslToRgb, type RGB, rgbHex, rgbToHsl } from './color.ts';
import { clamp } from './math.ts';
import { hashStr } from './random.ts';

export interface Palette {
  dominant: string;
  average: string;
  light: string;
  deep: string;
  hue: number;
  sat: number;
  lum: number;
  generated?: boolean;
}

interface Bin {
  w: number;
  r: number;
  g: number;
  b: number;
  s: number;
}

const paletteOf = (dom: RGB, avg: RGB): Palette => {
  const [h, s, l] = rgbToHsl(dom[0], dom[1], dom[2]);
  return {
    dominant: rgbHex(dom),
    average: rgbHex(avg),
    light: rgbHex(hslToRgb(h, clamp(s * 0.9, 0, 0.85), clamp(l + 0.26, 0.38, 0.88))),
    deep: rgbHex(hslToRgb(h, clamp(s * 1.05, 0, 0.92), clamp(l * 0.5, 0.07, 0.34))),
    hue: h,
    sat: s,
    lum: l,
  };
};

/** Deterministic palette from a string, used when the pixels are unreadable. */
export function fallbackPalette(seed: string): Palette {
  const h = hashStr(seed) % 360;
  const s = 0.42 + ((hashStr(`${seed}x`) % 100) / 100) * 0.3;
  return {
    dominant: rgbHex(hslToRgb(h, s, 0.52)),
    average: rgbHex(hslToRgb(h, s * 0.7, 0.5)),
    light: rgbHex(hslToRgb(h, s * 0.9, 0.76)),
    deep: rgbHex(hslToRgb(h, s, 0.22)),
    hue: h,
    sat: s,
    lum: 0.52,
    generated: true,
  };
}

/**
 * Dominant + accent colours of a drawable image. Returns `null` when the
 * canvas is tainted (cross-origin artwork without CORS headers) — callers
 * fall back to {@link fallbackPalette}.
 */
export function paletteFrom(source: CanvasImageSource, size = 32): Palette | null {
  try {
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const g = c.getContext('2d', { willReadFrequently: true });
    if (!g) return null;
    g.drawImage(source, 0, 0, size, size);
    const { data } = g.getImageData(0, 0, size, size);

    const bins = new Map<string, Bin>();
    let rs = 0;
    let gs = 0;
    let bs = 0;
    let n = 0;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] as number;
      const gg = data[i + 1] as number;
      const b = data[i + 2] as number;
      if ((data[i + 3] as number) < 128) continue;
      rs += r;
      gs += gg;
      bs += b;
      n++;
      const [h, s, l] = rgbToHsl(r, gg, b);
      if (l < 0.12 || l > 0.94) continue;
      const key = `${Math.round(h / 18)}|${Math.round(s * 4)}|${Math.round(l * 4)}`;
      const e = bins.get(key) ?? { w: 0, r: 0, g: 0, b: 0, s: 0 };
      const w = 1 + s * 2.4;
      e.w += w;
      e.r += r * w;
      e.g += gg * w;
      e.b += b * w;
      e.s += s * w;
      bins.set(key, e);
    }
    if (!n) return null;

    const avg: RGB = [rs / n, gs / n, bs / n];
    let best: Bin | null = null;
    const score = (e: Bin) => e.w * (0.4 + e.s / e.w);
    for (const e of bins.values()) if (!best || score(e) > score(best)) best = e;
    const dom: RGB = best ? [best.r / best.w, best.g / best.w, best.b / best.w] : avg;
    return paletteOf(dom, avg);
  } catch {
    return null; /* tainted canvas — the caller falls back to a neutral look */
  }
}
